import { motion } from "framer-motion";
import React from "react";
import {
  BiMessageRoundedDots,
  BiMicrophone,
  BiMicrophoneOff,
} from "react-icons/bi";
import { FaUsers } from "react-icons/fa";
import { GiHand } from "react-icons/gi";
import { VscSignOut } from "react-icons/vsc";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import Room from "../app/Room";
import { RootState, toggleMyHand } from "../app/store";
import { device } from "../globalAssets/breakpoints";
import { desktop } from "../globalAssets/fontsize";

const VoiceMenuContainer = () => {
  const { floatVoiceSettings, joinedRoom } = useAppSelector(
    (state: RootState) => state.room
  );

  return (
    <VoiceContainerHolder isShow={floatVoiceSettings && joinedRoom}>
      {floatVoiceSettings && joinedRoom && (
        <motion.div layoutId="voiceSettings">
          <VoiceMenuSettings />
        </motion.div>
      )}
    </VoiceContainerHolder>
  );
};

export const VoiceMenuSettings = () => {
  const {
    consumers,
    currentRoom,
    isMicMuted,
    isRoomAdmin,
    joinedRoom,
    producers,
    roomLoading,
    roomStatus,
    roomStatusMessage,
    userPriviledge,
    floatVoiceSettings,
    canSpeak,
    isSpeaking,
    usersInRoom,
    joinedRoomTitle,
    userHandRaised,
    reactions,
  } = useAppSelector((state: RootState) => state.room);

  const dispatch = useAppDispatch();

  const listeners = Object.keys(usersInRoom).length;
  const speakers = Object.values(usersInRoom).filter(
    (user) => user.isSpeaker
  ).length;

  function toggleUserHand() {
    if (!userHandRaised) {
      Room.raiseHand();
    } else {
      Room.dropHand();
    }
    dispatch(toggleMyHand());
  }

  function speakerOrUnMute() {
    if (canSpeak && !isSpeaking) {
      Room.produce("audioType");
    } else {
      if (isMicMuted) {
        Room.resumeProducer("audioType");
      } else {
        Room.pauseProducer("audioType");
      }
    }
  }

  return (
    <VoiceContainer>
      <UserAvatars />
      <TitleSection>
        <VoiceCommunityTitle title={joinedRoomTitle} />
        <IconNotifications speakers={speakers} listeners={listeners} />
      </TitleSection>
      <ActionMenuSection>
        <ActionMenu
          disabled={!joinedRoom}
          onClick={() => joinedRoom && Room.toggleReactionContainer()}
        >
          {/* <AiOutlineUsergroupAdd size={25} /> */}
          <div style={{ fontSize: 20 }}>{reactions[0]}</div>
        </ActionMenu>

        <ActionMenu
          disabled={!canSpeak || !joinedRoom}
          onClick={speakerOrUnMute}
        >
          {isMicMuted ? (
            <BiMicrophoneOff
              color={isSpeaking ? "#88cc55" : undefined}
              size={25}
            />
          ) : (
            <BiMicrophone
              color={isSpeaking ? "#88cc55" : undefined}
              size={25}
            />
          )}
        </ActionMenu>

        <ActionMenu disabled={!joinedRoom} onClick={toggleUserHand}>
          <GiHand size={25} color={userHandRaised ? "#a59a00" : undefined} />
        </ActionMenu>

        <ActionMenu
          disabled={!joinedRoom}
          onClick={() => Room.exit()}
          style={{ backgroundColor: "#99555533" }}
        >
          <VscSignOut size={25} color="red" />
        </ActionMenu>
      </ActionMenuSection>
    </VoiceContainer>
  );
};

const UserAvatars = () => {
  return <UserAvatarSection></UserAvatarSection>;
};

const IconNotifications = ({
  speakers,
  listeners,
}: {
  speakers: number;
  listeners: number;
}) => {
  return (
    <Row>
      <Row>
        <OptionText>{listeners}</OptionText>
        <FaUsers />
      </Row>
      <OptionText style={{ margin: "0 5px" }}>/</OptionText>
      <Row>
        <OptionText>{speakers}</OptionText>
        <BiMessageRoundedDots />
      </Row>
    </Row>
  );
};

const VoiceCommunityTitle = ({
  title = "--- --- --- ---- ---- ----",
}: {
  title?: string;
}) => {
  return <div style={{ textOverflow: "ellipsis" }}>{title}</div>;
};

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const OptionText = styled.div`
  font-weight: 700;
  margin: 0 1px;
  font-size: 15px;
`;

const VoiceContainerHolder = styled.div<{ isShow: boolean }>`
  position: fixed;
  top: 90vh;
  left: 0;
  width: 100%;
  height: 110px;
  display: ${(prop) => (prop.isShow ? "flex" : "none")};
  opacity: ${(prop) => (prop.isShow ? 1 : 0)};

  transition: opacity 500ms, display 500ms;
  justify-content: center;
  align-items: center;
  padding: 0px 0;
  &::before {
    content: "";
    position: absolute;
    padding: 10px 0;
    background-color: ${(props) => props.theme.fadeBg};
    width: 100%;
    height: 100%;
    filter: blur(10px);
    -webkit-filter: blur(10px);
  }
`;

const VoiceContainer = styled.div`
  height: 80px;
  padding: 5px 20px;
  width: 700px;
  transform: translateX(-0px);
  background: ${(props) => props.theme.foreground};
  /* background: red; */
  border-radius: 25px;
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.color};
  @media ${device.laptop} {
    font-size: ${desktop.small};
    .ant-input {
      font-size: ${desktop.small};
    }
  }
`;

const TitleSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${(props) => props.theme.color};
  @media ${device.laptop} {
    font-size: ${desktop.small};
    .ant-input {
      font-size: ${desktop.small};
    }
  }
`;

const UserAvatarSection = styled.div`
  display: flex;
  width: 70px;
  align-items: center;
  color: ${(props) => props.theme.color};
  @media ${device.laptop} {
    font-size: ${desktop.small};
    .ant-input {
      font-size: ${desktop.small};
    }
  }
`;

const ActionMenuSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media ${device.laptop} {
  }
`;

const ActionMenu = styled.div<{ disabled?: boolean }>`
  position: relative;
  width: 57px;
  height: 57px;
  margin: 0 7px;
  background: ${(props) => props.theme.foreground};
  /* background: pink; */
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  border-radius: 25%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => props.theme.color};
  :hover {
    background-color: #00000011;
  }
  &:after {
    position: absolute;
    content: "";
    width: 100%;
    height: 100%;
    background-color: #00000033;
    /* background-color: red; */
  }
  @media ${device.laptop} {
    font-size: ${desktop.small};
    .ant-input {
      font-size: ${desktop.small};
    }
  }
`;

export default VoiceMenuContainer;
